import React, { useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';

// Ultimate Landing Components
import NavbarUltimate from '../components/landing/NavbarUltimate';
import HeroSectionUltimate from '../components/landing/HeroSectionUltimate';
import TrustStripUltimate from '../components/landing/TrustStripUltimate';
import FeaturesBentoUltimate from '../components/landing/FeaturesBentoUltimate';
import PricingSectionUltimate from '../components/landing/PricingSectionUltimate';
import TestimonialsSectionUltimate from '../components/landing/TestimonialsSectionUltimate';
import FAQSectionUltimate from '../components/landing/FAQSectionUltimate';
import FinalCTASectionUltimate from '../components/landing/FinalCTASectionUltimate';
import FooterUltimate from '../components/landing/FooterUltimate';

// SEO Schema
const schemaOrg = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Cash-X Kassensystem",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "description": "Das moderne TSE-zertifizierte Kassensystem für die deutsche Gastronomie. Für Restaurants, Cafés, Bars und Lieferdienste.",
  "offers": {
    "@type": "Offer",
    "price": "49",
    "priceCurrency": "EUR",
    "priceValidUntil": "2025-12-31"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "127",
    "bestRating": "5"
  },
  "author": {
    "@type": "Organization",
    "name": "Cash-X GmbH",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "München",
      "addressCountry": "DE"
    }
  },
  "featureList": [
    "TSE-Zertifizierung",
    "KassenSichV-konform",
    "DSGVO-konform",
    "Multi-Station Küchensystem",
    "Lieferportal-Integration",
    "Offline-Modus",
    "24/7 Support"
  ]
};

export default function LandingUltimate() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Add schema to head
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaOrg);
    document.head.appendChild(script);
    
    // Update meta tags for SEO
    document.title = 'Cash-X | Das moderne Kassensystem für die Gastronomie';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'TSE-zertifiziertes Kassensystem für Restaurants, Cafés, Bars & Lieferdienste. KassenSichV-konform, DSGVO-sicher, Made in Germany. 14 Tage kostenlos testen!');
    }
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <TooltipProvider>
      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2F80FF] to-[#2DD4BF] origin-left z-[60]"
        style={{ scaleX }}
      />
      
      <div 
        data-testid="landing-page-ultimate"
        className="min-h-screen antialiased"
        style={{
          backgroundColor: '#0B1220',
          color: '#E8ECF2',
          fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif"
        }}
      >
        {/* Navigation */}
        <NavbarUltimate />
        
        {/* Main Content */}
        <main>
          {/* Hero with Aurora Background */}
          <HeroSectionUltimate />
          
          {/* Trust Strip with Infinite Logo Scroll */}
          <TrustStripUltimate />
          
          {/* Features with Bento Grid & 3D Cards */}
          <FeaturesBentoUltimate />
          
          {/* Pricing with Animated Border */}
          <PricingSectionUltimate />
          
          {/* Testimonials Carousel */}
          <TestimonialsSectionUltimate />
          
          {/* FAQ Accordion */}
          <FAQSectionUltimate />
          
          {/* Final CTA with Confetti */}
          <FinalCTASectionUltimate />
        </main>
        
        {/* Footer */}
        <FooterUltimate />
        
        {/* Toast Notifications */}
        <Toaster 
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#111C32',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#E8ECF2'
            }
          }}
        />
      </div>
    </TooltipProvider>
  );
}
